import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { Plus, Send, AlertCircle, Clock, Paperclip, X, Users, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StudentSelectorModal from './StudentSelectorModal';
import AnnouncementHistory from './AnnouncementHistory';
import TinyRichTextEditor from '@/components/shared/TinyRichTextEditor';

const AnnouncementBoard = () => {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [lastSentTime, setLastSentTime] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    category: 'general',
    priority: 'medium',
    targetType: 'all',
    target_classes: [],
    target_rooms: [],
    target_students: [],
    target_warden_id: '',
    scheduled_time: '',
    send_now: true,
    attachments: []
  });

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      // Fetch classes
      const classesRes = await apiClient.get(`/v1/admin/classes/?hostel_id=${user.hostel_id}`);
      const classesData = Array.isArray(classesRes.data) ? classesRes.data : [];
      setClasses(classesData);

      // Fetch students to extract unique rooms
      const studentsRes = await apiClient.get(`/v1/admin/students/?hostel_id=${user.hostel_id}`);
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : [];
      const uniqueRooms = [...new Set(studentsData.map(s => s.room_number).filter(Boolean))];
      setRooms(uniqueRooms.sort());

      // Fetch wardens if admin
      if (user.role === 'admin') {
        const wardensRes = await apiClient.get(`/v1/admin/wardens/?hostel_id=${user.hostel_id}`);
        setWardens(Array.isArray(wardensRes.data) ? wardensRes.data : []);
      }
    } catch (error) {
      toast.error('Failed to load metadata:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await apiClient.post('/v1/admin/uploads/attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...results]);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...results.map(r => r.url || r.view_url)]
      }));
      toast.success(`${results.length} file(s) uploaded to Cloudflare R2 successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload files to Cloudflare R2');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleStudentSelection = (students) => {
    setSelectedStudents(students);
    setFormData(prev => ({
      ...prev,
      target_students: students.map(s => s.id)
    }));
    setShowStudentModal(false);
  };

  const removeStudent = (studentId) => {
    setSelectedStudents(prev => prev.filter(s => s.id !== studentId));
    setFormData(prev => ({
      ...prev,
      target_students: prev.target_students.filter(id => id !== studentId)
    }));
  };

  const getRecipientCount = () => {
    // This is a rough estimate - actual count comes from backend
    if (formData.targetType === 'all') return 'All parents';
    if (formData.targetType === 'students') return `${selectedStudents.length} student(s)`;
    if (formData.targetType === 'classes') return `${formData.target_classes.length} class(es)`;
    if (formData.targetType === 'rooms') return `${formData.target_rooms.length} room(s)`;
    if (formData.targetType === 'warden') return 'Warden\'s students';
    return '0';
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return false;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return false;
    }
    if (formData.targetType === 'students' && selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return false;
    }
    if (formData.targetType === 'classes' && formData.target_classes.length === 0) {
      toast.error('Please select at least one class');
      return false;
    }
    if (formData.targetType === 'rooms' && formData.target_rooms.length === 0) {
      toast.error('Please select at least one room');
      return false;
    }
    if (formData.targetType === 'warden' && !formData.target_warden_id) {
      toast.error('Please select a warden');
      return false;
    }
    if (!formData.send_now && !formData.scheduled_time) {
      toast.error('Please select a scheduled time');
      return false;
    }

    // Duplicate prevention check
    if (lastSentTime && formData.send_now) {
      const timeDiff = Date.now() - lastSentTime;
      if (timeDiff < 60000) { // 1 minute
        toast.error('Please wait before sending another announcement');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const recipientText = getRecipientCount();
    const confirmed = window.confirm(
      `Send ${formData.send_now ? 'now' : 'scheduled'} announcement to ${recipientText}?\n\nCategory: ${formData.category}\nPriority: ${formData.priority}`
    );
    
    if (!confirmed) return;

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        message: formData.message,
        category: formData.category,
        priority: formData.priority,
        hostel_id: user.hostel_id,
        target_all: formData.targetType === 'all',
        target_classes: formData.targetType === 'classes' ? formData.target_classes : [],
        target_rooms: formData.targetType === 'rooms' ? formData.target_rooms : [],
        target_students: formData.targetType === 'students' ? formData.target_students : [],
        target_warden_id: formData.targetType === 'warden' ? formData.target_warden_id : null,
        scheduled_time: formData.send_now ? null : formData.scheduled_time,
        send_now: formData.send_now,
        attachments: formData.attachments
      };

      const response = await apiClient.post('/v1/admin/announcements/', payload);
      
      toast.success(
        formData.send_now 
          ? `Announcement sent to ${response.data.recipient_count} recipients!`
          : 'Announcement scheduled successfully!'
      );
      
      setLastSentTime(Date.now());
      resetForm();
      setShowCreateForm(false);
      setRefreshHistory(prev => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      category: 'general',
      priority: 'medium',
      targetType: 'all',
      target_classes: [],
      target_rooms: [],
      target_students: [],
      target_warden_id: '',
      scheduled_time: '',
      send_now: true,
      attachments: []
    });
    setSelectedStudents([]);
    setUploadedFiles([]);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-orange-600 bg-orange-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
            Notice Board
          </h1>
          <p className="text-gray-600 mt-1">Send announcements to parents</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showCreateForm ? 'Hide Form' : 'Create Announcement'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Category & Priority */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="general">General</option>
                <option value="financial">Financial</option>
                <option value="holidays">Holidays</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority *</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Upcoming Sports Day"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium mb-2">Message *</label>
            <TinyRichTextEditor
              value={formData.message}
              onChange={(value) => setFormData((prev) => ({ ...prev, message: value }))}
              placeholder="Write your announcement here..."
              minHeight="150px"
              required
            />
          </div>

          {/* Send To */}
          <div>
            <label className="block text-sm font-medium mb-2">Send To *</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="all"
                  checked={formData.targetType === 'all'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="w-4 h-4"
                />
                <span>All Parents</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="classes"
                  checked={formData.targetType === 'classes'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="w-4 h-4"
                />
                <span>Specific Class(es)</span>
              </label>
              {formData.targetType === 'classes' && (
                <div className="ml-6 space-y-2">
                  {classes.map(cls => (
                    <label key={cls.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.target_classes.includes(cls.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              target_classes: [...prev.target_classes, cls.name]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              target_classes: prev.target_classes.filter(c => c !== cls.name)
                            }));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span>{cls.name}</span>
                    </label>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="rooms"
                  checked={formData.targetType === 'rooms'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="w-4 h-4"
                />
                <span>Specific Room(s)</span>
              </label>
              {formData.targetType === 'rooms' && (
                <div className="ml-6">
                  <select
                    multiple
                    value={formData.target_rooms}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, target_rooms: selected });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    size={5}
                  >
                    {rooms.map(room => (
                      <option key={room} value={room}>{room}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="students"
                  checked={formData.targetType === 'students'}
                  onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                  className="w-4 h-4"
                />
                <span>Individual Students</span>
              </label>
              {formData.targetType === 'students' && (
                <div className="ml-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowStudentModal(true)}
                    className="mb-2"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Select Students ({selectedStudents.length})
                  </Button>
                  {selectedStudents.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedStudents.map(student => (
                        <span
                          key={student.id}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                        >
                          {student.name}
                          <button
                            type="button"
                            onClick={() => removeStudent(student.id)}
                            className="hover:text-blue-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {user.role === 'admin' && wardens.length > 0 && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="warden"
                      checked={formData.targetType === 'warden'}
                      onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span>Warden's Students</span>
                  </label>
                  {formData.targetType === 'warden' && (
                    <div className="ml-6">
                      <select
                        value={formData.target_warden_id}
                        onChange={(e) => setFormData({ ...formData, target_warden_id: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Warden</option>
                        {wardens.map(warden => (
                          <option key={warden.id} value={warden.id}>
                            {warden.name} ({warden.assigned_student_count || 0} students)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium mb-1.5 text-gray-800">
              Attachments (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-200 hover:border-[#2C5F7F]/50 rounded-xl p-5 bg-gray-50/50 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="file-upload"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer space-y-1.5"
              >
                <div className="p-2.5 bg-blue-50 text-[#2C5F7F] rounded-full">
                  <Paperclip className="w-6 h-6" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {uploading ? 'Uploading to Cloudflare R2...' : 'Click to browse files to attach'}
                </span>
                <span className="text-xs text-gray-500">
                  PDF, Images, Documents up to 25MB (securely stored in Cloudflare R2)
                </span>
              </label>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded-lg shadow-2xs">
                    <div className="flex items-center gap-2 truncate">
                      <Paperclip className="w-4 h-4 text-[#2C5F7F] shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">{file.filename}</span>
                      {file.size && (
                        <span className="text-xs text-gray-400">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scheduling */}
          <div>
            <label className="block text-sm font-medium mb-2">Schedule</label>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={formData.send_now}
                  onChange={() => setFormData({ ...formData, send_now: true, scheduled_time: '' })}
                  className="w-4 h-4"
                />
                <span>Send Now</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!formData.send_now}
                  onChange={() => setFormData({ ...formData, send_now: false })}
                  className="w-4 h-4"
                />
                <span>Schedule for Later</span>
              </label>
              {!formData.send_now && (
                <div className="ml-6">
                  <input
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                    className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Recipient Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-800">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Will be sent to: {getRecipientCount()}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                setShowCreateForm(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || uploading}
              className="btn-primary text-white"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending...
                </>
              ) : formData.send_now ? (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Now
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {/* History */}
      <AnnouncementHistory hostelId={user.hostel_id} refreshTrigger={refreshHistory} />

      {/* Student Selector Modal */}
      <StudentSelectorModal
        isOpen={showStudentModal}
        onClose={() => setShowStudentModal(false)}
        onConfirm={handleStudentSelection}
        hostelId={user.hostel_id}
        preselected={formData.target_students}
      />
    </div>
  );
};

export default AnnouncementBoard;
